import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import { PDFDownloadLink } from "@react-pdf/renderer"
import { Button, Modal, Spin } from "antd"
import { BulbOutlined, CloseOutlined } from "@ant-design/icons"
import "../../../assets/stylesheets/rubrics/implementation/plan.scss"
import pdfSvg from "../../../assets/images/pdf-file.svg"
import SchoolPlanPdf from "./SchoolPlanPdf"
import riePlanning from "../../../assets/images/rie-plan.png"
import sendIcon from "../../../assets/images/send.png"
import VoiceInput from "../shared/VoiceInput"

const RiaChatSection = ({ rubricId, indicator }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm Rei 👋 Ask me anything about this indicator — I'm here to help you reflect and plan next steps.",
    },
  ])
  const [input, setInput] = useState("")
  const [chatLoading, setChatLoading] = useState(false)
  const [isVoiceRecording, setIsVoiceRecording] = useState(false)
  const messagesRef = useRef(null)
  const bottomRef = useRef(null)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return undefined
    }
    const timer = setTimeout(() => {
      if (messagesRef.current) {
        messagesRef.current.scrollTo({
          top: messagesRef.current.scrollHeight,
          behavior: "smooth",
        })
      }
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [messages])

  const sendMessage = () => {
    const text = input.trim()
    if (!text || chatLoading) return

    const userMsg = { role: "user", content: text }
    const history = messages
      .slice(1)
      .map((m) => ({ role: m.role, content: m.content }))
    setMessages((prev) => [...prev, userMsg])
    setInput("")
    setChatLoading(true)

    // eslint-disable-next-line no-undef
    APIClient.post(
      `/implementation_rubrics/${rubricId}/plan_chat`,
      { message: text, history, indicator_id: indicator.id },
      (response) => {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: response.reply },
        ])
        setChatLoading(false)
      },
      () => {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, I ran into an issue. Please try again.",
          },
        ])
        setChatLoading(false)
      },
    )
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleVoiceTranscription = (text) => {
    setInput((prev) => (prev ? `${prev} ${text}` : text))
  }

  return (
    <div className="rie-chat">
      <div className="rie-chat-header">
        <span className="label">Ask Rei about this Indicator</span>
        <img src={riePlanning} className="rie-chat-header-icon" alt="Rei" />
      </div>

      <div className="rie-chat-messages" ref={messagesRef}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`rie-chat-message-row rie-chat-message-row--${msg.role}`}
          >
            {msg.role === "assistant" && (
              <div className="rie-chat-avatar">
                <img src={riePlanning} alt="Rei" />
              </div>
            )}
            <div className={`rie-chat-bubble rie-chat-bubble--${msg.role}`}>
              {msg.role === "assistant" && (
                <span className="rie-chat-sender">Rei</span>
              )}
              {msg.content}
            </div>
          </div>
        ))}
        {chatLoading && (
          <div className="rie-chat-typing">
            <div className="rie-chat-avatar">
              <img src={riePlanning} alt="Rei" />
            </div>
            <div className="loader-css" />
          </div>
        )}
      </div>

      <div className="rie-chat-input-row">
        <div className="rie-chat-input-wrapper">
          <input
            type="text"
            className="rie-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Rei about this indicator..."
            disabled={chatLoading}
          />
          <div className="rie-chat-input-icons">
            <VoiceInput
              onTranscriptionComplete={handleVoiceTranscription}
              onRecordingStateChange={(recording) => setIsVoiceRecording(recording)}
              disabled={chatLoading}
              disabledTooltipMessage="Voice Input Disabled During AI Response Generation"
            />
          </div>
        </div>
        <button
          className="rie-chat-send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || chatLoading || isVoiceRecording}
        >
          <img src={sendIcon} alt="Send" />
        </button>
      </div>

      <div ref={bottomRef} />
    </div>
  )
}

function ImplementationPlan({
  rubric,
  reportHeadingTop,
  removeHtmlTags,
  currentSchoolName,
  dateForReport,
  currentschoolLogo,
  onOpenPlanSummary,
}) {
  const [selectedIndicator, setSelectedIndicator] = useState(null)
  const [aiSuggestion, setAiSuggestion] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [cursorTooltip, setCursorTooltip] = useState({ visible: false, x: 0, y: 0 })

  const prefetchCache = useRef({})
  const prefetchTimers = useRef({})

  const prefetchIndicator = (indicator) => {
    if (prefetchCache.current[indicator.id] !== undefined) return
    if (!indicator.explanation || indicator.explanation.trim().length === 0) return

    prefetchTimers.current[indicator.id] = setTimeout(() => {
      delete prefetchTimers.current[indicator.id]
      fetch(`/api/v1/rubric_indicators/${indicator.id}/suggest_score`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": (document.querySelector("[name=\"csrf-token\"]") || {})
            .content,
        },
      })
        .then((r) => r.json())
        .then((data) => {
          if (!data.error) prefetchCache.current[indicator.id] = data
        })
        .catch(() => {})
    }, 300)
  }

  const cancelPrefetch = (indicator) => {
    if (prefetchTimers.current[indicator.id]) {
      clearTimeout(prefetchTimers.current[indicator.id])
      delete prefetchTimers.current[indicator.id]
    }
  }

  const sortArrayByRating = (array) => array
    .flatMap((stage) => stage.indicators.map((indicator) => ({
      id: indicator.id,
      index: indicator.index,
      label: indicator.label,
      explanation: indicator.explanation,
      rating: indicator.rating,
    })))
    .sort((a, b) => {
      if (a.rating === null && b.rating === null) return 0
      if (a.rating === null) return 1
      if (b.rating === null) return -1
      if (a.rating === 0 && b.rating === 0) return 0
      if (a.rating === 0) return 1
      if (b.rating === 0) return -1
      return b.rating - a.rating
    })

  const handleIndicatorClick = async (indicator) => {
    setSelectedIndicator(indicator)
    setAiSuggestion(null)
    setFetchError(null)
    setIsModalVisible(true)

    if (!indicator.explanation || indicator.explanation.trim().length === 0) {
      return
    }

    const cached = prefetchCache.current[indicator.id]
    if (cached) {
      setAiSuggestion(cached)
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch(
        `/api/v1/rubric_indicators/${indicator.id}/suggest_score`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": (
              document.querySelector("[name=\"csrf-token\"]") || {}
            ).content,
          },
        },
      )
      const data = await response.json()
      if (data.error) {
        setFetchError(data.error)
      } else {
        setAiSuggestion(data)
      }
    } catch (e) {
      setFetchError("Failed to get AI suggestion. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const closeModal = () => {
    setIsModalVisible(false)
    setAiSuggestion(null)
    setSelectedIndicator(null)
    setFetchError(null)
  }

  const hasExplanation = selectedIndicator
    && selectedIndicator.explanation
    && selectedIndicator.explanation.trim().length > 0

  const CARD_CONFIG = [
    { key: "strengths" },
    { key: "gaps" },
    { key: "guidance" },
  ]

  const suggestionCards = aiSuggestion
    ? CARD_CONFIG.filter((c) => Boolean(aiSuggestion[c.key])).map((c) => ({
      ...c,
      content: aiSuggestion[c.key],
    }))
    : []

  const GUIDANCE_TIER_PREFIXES = [
    "First — Do this now:",
    "Then — Do this next:",
    "Later — Do this over time:",
  ]

  const parseGuidanceTiers = (text) => {
    const positions = GUIDANCE_TIER_PREFIXES.map((prefix, i) => ({
      key: i,
      prefix,
      idx: text.indexOf(prefix),
    }))
      .filter((t) => t.idx !== -1)
      .sort((a, b) => a.idx - b.idx)

    if (positions.length === 0) return null

    return positions.map((tier, i) => {
      const contentStart = tier.idx + tier.prefix.length
      const nextTier = positions[i + 1]
      const content = nextTier
        ? text.slice(contentStart, nextTier.idx).trim()
        : text.slice(contentStart).trim()
      return { key: tier.key, content }
    })
  }

  const renderPlainText = (text) => {
    const lines = text
      .split("\n")
      .map((l) => l.trim().replace(/^-\s*/, ""))
      .filter(Boolean)
    return (
      <>
        {lines.map((line, i) => (
          <p key={i} style={{ margin: "0 0 4px", fontSize: 13 }}>
            {line}
          </p>
        ))}
      </>
    )
  }

  const renderCardContent = (card) => {
    if (card.key === "guidance") {
      const tiers = parseGuidanceTiers(card.content)
      if (tiers) {
        return tiers.map((tier, i) => (
          <div
            key={tier.key}
            style={{ marginBottom: i < tiers.length - 1 ? 10 : 0 }}
          >
            {renderPlainText(tier.content)}
          </div>
        ))
      }
    }
    return renderPlainText(card.content)
  }

  return (
    <>
      <div
        className="newPdfButton"
        style={{ display: "flex", alignItems: "center", gap: "12px" }}
      >
        {onOpenPlanSummary && (
          <Button
            icon={<BulbOutlined />}
            onClick={onOpenPlanSummary}
            className="btnRubric"
            style={{
              backgroundColor: "#0CA4A5",
              color: "#FFFFFF",
              border: "none",
              height: "35px",
              display: "flex",
              alignItems: "center",
              fontFamily: "Archivo Narrow",
              width: "190px",
              borderRadius: "6px",
            }}
          >
            Plan Implications Summary
          </Button>
        )}
        <PDFDownloadLink
          document={(
            <SchoolPlanPdf
              rubric={rubric}
              sortArrayByRating={sortArrayByRating}
              reportHeadingTop={reportHeadingTop}
              removeHtmlTags={removeHtmlTags}
              currentschoolLogo={currentschoolLogo}
            />
          )}
          fileName={`${
            currentSchoolName || "school_name"
          }_plan(${dateForReport}).pdf`}
        >
          {() => <img style={{ width: "40px" }} src={pdfSvg} />}
        </PDFDownloadLink>
      </div>

      <div>
        <div className="plan p-3 mb-4">
          <div className="narrative1">
            {sortArrayByRating(rubric.plan).map((indicator, n) => (
              <div
                key={n}
                className={`pl-4 pr-4 pt-2 pb-2 borderBottom plan-indicator-tile ${
                  indicator.rating === 0
                  || indicator.rating === 1
                  || indicator.rating === null
                    ? "lightGreen"
                    : indicator.rating === 2 || indicator.rating === 3
                      ? "mediumGreen"
                      : indicator.rating === 4
                        ? "darkGreen"
                        : ""
                } ${n === 0 ? "rounded-top" : ""} ${
                  n === rubric.plan.length - 1 ? "rounded-bottom" : ""
                }`}
                onClick={() => handleIndicatorClick(indicator)}
                onMouseEnter={(e) => {
                  prefetchIndicator(indicator)
                  setCursorTooltip({ visible: true, x: e.clientX, y: e.clientY })
                }}
                onMouseLeave={() => {
                  cancelPrefetch(indicator)
                  setCursorTooltip((prev) => ({ ...prev, visible: false }))
                }}
                onMouseMove={(e) => setCursorTooltip({ visible: true, x: e.clientX, y: e.clientY })}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleIndicatorClick(indicator)}
              >
                <span className="fontWeight600 anchorColor">
                  {indicator.index}.{" "}
                  <span
                    dangerouslySetInnerHTML={{ __html: indicator.label }}
                  />
                </span>
                <span className="italicFonts displayBlock">
                  {indicator.explanation}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {cursorTooltip.visible && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            left: cursorTooltip.x + 14,
            top: cursorTooltip.y - 38,
            background: "rgba(0,0,0,0.75)",
            color: "#fff",
            padding: "5px 10px",
            borderRadius: "6px",
            fontSize: "14px",
            pointerEvents: "none",
            zIndex: 9999,
            whiteSpace: "nowrap",
          }}
        >
          Click to view Rei&apos;s analysis for this indicator
        </div>,
        document.body,
      )}

      <Modal
        open={isModalVisible}
        onCancel={closeModal}
        footer={null}
        width={660}
        centered
        closable={false}
        className="pis-modal-wrapper"
      >
        <div className="pis-modal-header">
          <div>
            <div className="pis-modal-title">
              Rei-Assisted Indicator Analysis
            </div>
            <div className="pis-modal-subtitle">
              A focused review of proposed improvements and adjustments
            </div>
          </div>
          {/* <button className="pis-modal-close" onClick={closeModal}>
            <CloseOutlined />
          </button> */}
          <button className="pis-modal-close" onClick={closeModal}>
            <CloseOutlined />
          </button>
        </div>

        {isLoading && (
          <div className="pis-modal-loading">
            <Spin size="large" />
            <p>Analyzing indicator...</p>
          </div>
        )}

        {!isLoading && fetchError && (
          <div className="pis-modal-error">{fetchError}</div>
        )}

        {!isLoading && !fetchError && selectedIndicator && (
          <div className="pis-modal-content-box-main">
            <div className="pis-modal-content-box-main-inner">
              <div className="pis-modal-icon-wrapper">
                <img
                  src={riePlanning}
                  className="riePlanning-icon"
                  style={{ width: "171px", height: "183px" }}
                />
              </div>
              <div className="pis-modal-teal-label">
                <span>Indicator Insight: {selectedIndicator.index}.</span>
              </div>
              <div
                className="pis-modal-i
              nner-content"
              >
                {!hasExplanation ? (
                  <div className="pis-modal-content-box">
                    <p className="pis-modal-content-text">
                      No description has been added for this indicator yet.
                      Please add a response in the rubric section to receive an
                      AI suggestion.
                    </p>
                  </div>
                ) : (
                  <>
                    {aiSuggestion && (
                      <div className="pis-modal-content-box">
                        <p className="pis-modal-content-text">
                          {aiSuggestion.reasoning}
                        </p>
                      </div>
                    )}

                    {aiSuggestion && suggestionCards.length > 0 && (
                      <>
                        <div className="pis-modal-suggestion-label">
                          Rei&apos;s suggestion
                        </div>
                        <div className="pis-modal-cards">
                          {suggestionCards.map((card) => (
                            <div key={card.key} className="pis-plan-indicator-card">
                              {renderCardContent(card)}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
                <RiaChatSection
                  key={selectedIndicator.id}
                  rubricId={rubric.id}
                  indicator={selectedIndicator}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default ImplementationPlan
